import { ChunkCitation, UIStructuredOutput } from "../generated/core-api";

/**
 * SSE Streaming Types for Completion Endpoint
 *
 * These types define the Server-Sent Events protocol for the streaming
 * completion endpoint: POST /api/v1/workspace/{workspace_id}/thread/{thread_id}/completion/stream
 */

/**
 * Processing phases during completion
 */
export type StreamPhase =
  | "preparing"
  | "searching"
  | "reranking"
  | "generating"
  | "complete";

/**
 * Progress event payload - sent multiple times during processing
 */
export interface ProgressEventPayload {
  chunk_type: "progress";
  phase: StreamPhase;
  step: string;
  message: string;
  progress_percent: number;
  elapsed_ms: number;
  details?: {
    grounded?: boolean;
    sources_count?: number;
    reason?: string;
  };
}

/**
 * Source preview shown before LLM generates response
 */
export interface SourcePreview {
  org: string;
  org_category: string;
  document: string;
  preview: string;
}

/**
 * Sources found event payload - sent once after RAG retrieval completes
 */
export interface SourcesFoundEventPayload {
  chunk_type: "sources_found";
  sources: SourcePreview[];
  total_chunks: number;
  orgs_searched: number;
}

/**
 * Metadata event payload - citation details sent separately
 */
export interface MetadataEventPayload {
  citations: ChunkCitation[];
}

/**
 * Union type for all SSE events from the streaming completion endpoint
 * Format: [event_type, payload]
 */
export type SSEEvent =
  | ["progress", ProgressEventPayload]
  | ["sources_found", SourcesFoundEventPayload]
  | ["result", UIStructuredOutput]
  | ["metadata", MetadataEventPayload]
  | ["end", string]
  | ["error", string];

/**
 * Type guard for progress events
 */
export function isProgressEvent(
  event: SSEEvent
): event is ["progress", ProgressEventPayload] {
  return event[0] === "progress";
}

/**
 * Type guard for sources found events
 */
export function isSourcesFoundEvent(
  event: SSEEvent
): event is ["sources_found", SourcesFoundEventPayload] {
  return event[0] === "sources_found";
}

/**
 * Type guard for result events
 */
export function isResultEvent(
  event: SSEEvent
): event is ["result", UIStructuredOutput] {
  return event[0] === "result";
}

/**
 * Type guard for metadata events
 */
export function isMetadataEvent(
  event: SSEEvent
): event is ["metadata", MetadataEventPayload] {
  return event[0] === "metadata";
}

/**
 * Type guard for end events
 */
export function isEndEvent(event: SSEEvent): event is ["end", string] {
  return event[0] === "end";
}

/**
 * Type guard for error events
 */
export function isErrorEvent(event: SSEEvent): event is ["error", string] {
  return event[0] === "error";
}

/**
 * Phase display configuration for UI
 */
export const PHASE_CONFIG: Record<
  StreamPhase,
  { message: string; icon: string }
> = {
  preparing: { message: "Analyzing your question...", icon: "search" },
  searching: { message: "Searching knowledge bases...", icon: "database" },
  reranking: { message: "Finding the best matches...", icon: "filter" },
  generating: { message: "Crafting your answer...", icon: "sparkles" },
  complete: { message: "Done!", icon: "check" },
};
